/* eslint-disable @next/next/no-img-element */
import { Button, Loading, TextInput } from "@climate-science/component-library";
import { useForm } from "react-hook-form";
import { useTranslation } from "next-i18next";
import React, { useEffect, useRef, useState } from "react";
import { getDownloadURL, ref } from "@firebase/storage";
import styles from "./CertificateGenerator.module.scss";

import { getStorage } from "firebase/storage";
import { NextSeo } from "next-seo";
import html2canvas from "html2canvas";
import { Course } from "@cs/lib/models/skeleton.model";
import { useCourseProgress } from "@cs/components/hooks/useCourseProgress";
import Custom404 from "../_app/custom404";


const saveAs = (blob: string, fileName: string) => {
  const elem = window.document.createElement('a')
  elem.setAttribute('style', 'display: none')
  elem.href = blob
  elem.download = fileName;
  (document.body || document.documentElement).appendChild(elem)
  if (typeof elem.click === 'function') {
    elem.click();
  } else {
    elem.target = '_blank';
    elem.dispatchEvent(new MouseEvent('click', {
      view: window,
      bubbles: true,
      cancelable: true
    }));
  }
  URL.revokeObjectURL(elem.href);
  elem.remove()
}


export interface CertificateGeneratorProps {
  courseId: string,
  courses: Course[]
  basic: 'Basics' | 'Details' 
}

type CertificateGeneratorForm = {
  name: string
}

const date = new Date().getDate().toString().padStart(2, '0') + '.' + 
(new Date().getMonth() + 1).toString().padStart(2, '0') + '.' +
new Date().getFullYear().toString();

const CertificateGenerator: React.FC<CertificateGeneratorProps> = ({
  courseId,
  courses,
  basic
}) => {
  const {t} = useTranslation('certificate')
  const [certURL, setCertURL] = useState<string>()
  const [downloading, setDownloading] = useState<boolean>(false)
  const certificateRef = useRef<HTMLDivElement>(null)

  const {
    register,
    handleSubmit,
    watch,
    formState: {errors}
  } = useForm<CertificateGeneratorForm>({mode: "onChange"})

  const course = courses.find((element) =>  element.courseId == courseId)
  const chapNum = course?.chapters[basic].length || 0
  const progress = useCourseProgress(courseId, chapNum + 1, basic)

  useEffect(() => {
    if (!courseId) return
    const certificateReference = ref(getStorage(), `CourseCertificates/Certificate_${courseId}.jpg`)

    getDownloadURL(certificateReference).then(url => {
      setCertURL(url)
    })
      .catch(() => {
        setCertURL(undefined)
      })
  }, [courseId])

  const name = watch('name', '')


  const download = () => {
    if (!certificateRef.current) return
    setDownloading(true)
    html2canvas(certificateRef.current, {useCORS: true})
      .then((canvas: HTMLCanvasElement) => {
        const image = canvas.toDataURL('image/png', 1.0);
        saveAs(image, `certificate-${courseId}-${name}.png`) 
      })
      .finally(() => setDownloading(false))
  }

  if ( progress === 0 ) return (
    <main className={styles.loadingContainer}>
      <Loading />
    </main>
  )

  if ( progress < 100 ) return <Custom404/>

  return (
    <section className={styles.certificateGenerator}>
      <NextSeo 
        title={t('certificate')}
      />
      <h1 className={styles.title}>{t('print-cert')}</h1>
      <form onSubmit={handleSubmit(download)} className={styles.form}>
        <TextInput 
          placeholder={t('enter-name')}
          {...register('name')}
          error={errors.name ? errors.name.message : undefined}
          maxLength={30}
          labelClassName={styles.textInput}
          disabled={downloading}
        >
          {t('your-name')}
        </TextInput>
        <div className={styles.certificateWrapper} ref={certificateRef}>
          <div className={styles.certificate}>
            {
              certURL && <img src={certURL} alt="certificate" className={styles.certImage}/>
            }
            <p className={styles.name}>{name}</p>
            <p className={styles.date}>{date}</p>
          </div>
        </div>
        <Button type="submit" primary disabled={downloading}>
          {downloading ? t('downloading') : t('download')}
        </Button>
      </form>
    </section>
  )
}

export default CertificateGenerator